import { useState, useEffect, useCallback } from 'react';
import { Trash2, Reply } from 'lucide-react';
import api from '../../services/api';

const statuses = ['new', 'read', 'replied', 'resolved', 'spam'];

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [reply, setReply] = useState('');

  const fetchContacts = useCallback(async () => {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const res = await api.get(`/contact${params}`);
      setContacts(res.data.contacts);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const updateStatus = async (id, status) => {
    await api.put(`/contact/${id}/status`, { status });
    fetchContacts();
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    await api.put(`/contact/${selectedContact._id}/reply`, { reply });
    setReply('');
    setSelectedContact(null);
    fetchContacts();
  };

  const deleteContact = async (id) => {
    if (window.confirm('Delete this inquiry?')) {
      await api.delete(`/contact/${id}`);
      fetchContacts();
    }
  };

  if (loading) return <div className="animate-pulse h-96 bg-gray-200 rounded-xl"></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Inquiries</h2>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600">
          <option value="">All Status</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700"><tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {contacts.map((c) => (
              <tr key={c._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4">
                  <p className="font-medium text-gray-800 dark:text-white">{c.name}</p>
                  {c.company && <p className="text-sm text-gray-500">{c.company}</p>}
                </td>
                <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{c.email}</td>
                <td className="px-6 py-4 text-gray-600 dark:text-gray-300 max-w-xs truncate">{c.subject}</td>
                <td className="px-6 py-4">
                  <select value={c.status} onChange={(e) => updateStatus(c._id, e.target.value)} className={`px-2 py-1 text-xs rounded border ${c.status === 'new' ? 'bg-green-100' : c.status === 'replied' ? 'bg-blue-100' : c.status === 'resolved' ? 'bg-gray-100' : 'bg-yellow-100'}`}>
                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-6 py-4 text-right space-x-2">
                  <button onClick={() => setSelectedContact(c)} className="p-2 text-gray-500 hover:text-cyan-600"><Reply size={16} /></button>
                  <button onClick={() => deleteContact(c._id)} className="p-2 text-gray-500 hover:text-red-600"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {contacts.length === 0 && <div className="p-8 text-center text-gray-500">No inquiries found</div>}
      </div>

      {selectedContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg p-6">
            <h3 className="text-xl font-semibold mb-4">Reply to {selectedContact.name}</h3>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4 max-h-48 overflow-y-auto">
              <p className="text-sm text-gray-600 dark:text-gray-300">{selectedContact.message}</p>
            </div>
            <textarea placeholder="Type your reply..." value={reply} onChange={(e) => setReply(e.target.value)} className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" rows={4} />
            <div className="flex gap-4 mt-4">
              <button onClick={sendReply} className="flex-1 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">Send Reply</button>
              <button onClick={() => setSelectedContact(null)} className="flex-1 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}